/**
 * BulkResyncResults — renders the full result after a bulk resync completes.
 * Sections: summary banner · KPI grid · per-industry push table ·
 *           per-client matrix · raw JSON details.
 */
import React from 'react';
import { CheckCircle2, AlertTriangle, Clock, Server } from 'lucide-react';
import { useT } from '../../i18n/I18nContext.jsx';

// ── Micro-components ────────────────────────────────────────────────────────
function Kpi({ label, value, accent }) {
  return (
    <div style={{
      background: 'var(--surface-2)', padding: '12px 14px', borderRadius: 8,
      border: '1px solid var(--border)', borderLeft: `3px solid ${accent}`
    }}>
      <div style={{ fontSize: 9, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 900, color: 'var(--text-primary)', marginTop: 2 }}>{value}</div>
    </div>
  );
}

function Th({ children, align }) {
  return (
    <th style={{ padding: '10px 12px', textAlign: align || 'left', fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
      {children}
    </th>
  );
}

function Td({ children, align }) {
  return (
    <td style={{ padding: '10px 12px', textAlign: align || 'left', color: 'var(--text-primary)' }}>
      {children}
    </td>
  );
}

// ── Per-industry table ───────────────────────────────────────────────────────
function PerIndustryTable({ tier, t }) {
  if (!tier.perIndustry || Object.keys(tier.perIndustry).length === 0) return null;
  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }} data-testid="bulk-resync-per-industry">
      <div style={{
        background: 'var(--surface-3)', padding: '8px 12px',
        fontSize: 10, fontWeight: 700, color: 'var(--text-muted)',
        textTransform: 'uppercase', letterSpacing: '0.06em',
        display: 'flex', alignItems: 'center', gap: 6
      }}>
        <Server size={11} />
        {t('erp.bulkResync.perIndustryTitle', 'Per-Industry Push to')} {tier.targetDB || 'RC26erp_dev'} · {Object.keys(tier.perIndustry).length} {t('erp.bulkResync.industries', 'industries')}
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead>
          <tr style={{ background: 'var(--surface-2)' }}>
            <Th>{t('erp.bulkResync.col.industry', 'Industry')}</Th>
            <Th align="right">{t('erp.bulkResync.col.modules', 'Modules')}</Th>
            <Th align="right">{t('erp.bulkResync.col.subModules', 'Sub-Modules')}</Th>
            <Th align="right">{t('erp.bulkResync.col.screens', 'Screens')}</Th>
            <Th align="right">{t('erp.bulkResync.col.totalRows', 'Total Rows')}</Th>
          </tr>
        </thead>
        <tbody>
          {Object.entries(tier.perIndustry)
            .sort(([a], [b]) => a === 'SHARED' ? -1 : b === 'SHARED' ? 1 : a.localeCompare(b))
            .map(([code, counts]) => {
              const total = (counts.modules || 0) + (counts.subModules || 0) + (counts.screens || 0);
              return (
                <tr key={code} style={{ borderTop: '1px solid var(--border)' }} data-testid={`bulk-industry-${code}`}>
                  <Td><span className={`badge ${code === 'SHARED' ? 'badge-amber' : 'badge-blue'}`}>{code}</span></Td>
                  <Td align="right">{counts.modules    ?? 0}</Td>
                  <Td align="right">{counts.subModules ?? 0}</Td>
                  <Td align="right">{counts.screens    ?? 0}</Td>
                  <Td align="right"><strong>{total}</strong></Td>
                </tr>
              );
            })}
        </tbody>
      </table>
    </div>
  );
}

// ── Per-client table ─────────────────────────────────────────────────────────
function PerClientTable({ clientResults, t }) {
  if (!Array.isArray(clientResults) || clientResults.length === 0) return null;
  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }} data-testid="bulk-resync-table">
        <thead>
          <tr style={{ background: 'var(--surface-3)' }}>
            <Th>{t('erp.bulkResync.col.client',     'Client')}</Th>
            <Th>{t('erp.bulkResync.col.tier',       'Tier')}</Th>
            <Th>{t('erp.bulkResync.col.status',     'Status')}</Th>
            <Th align="right">{t('erp.bulkResync.col.modules',    'Modules')}</Th>
            <Th align="right">{t('erp.bulkResync.col.subModules', 'Sub-Modules')}</Th>
            <Th align="right">{t('erp.bulkResync.col.screens',    'Screens')}</Th>
            <Th>{t('erp.bulkResync.col.hash',       'Hash')}</Th>
            <Th>{t('erp.bulkResync.col.result',     'Result')}</Th>
          </tr>
        </thead>
        <tbody>
          {clientResults.map((r, idx) => (
            <tr key={idx} style={{ borderTop: '1px solid var(--border)' }} data-testid={`bulk-row-${idx}`}>
              <Td>
                <div style={{ fontWeight: 700, color: 'var(--text-primary)' }}>{r.clientName || '—'}</div>
                <div style={{ fontSize: 10, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>{r.clientId || '∅'}</div>
              </Td>
              <Td><span className="badge badge-blue">{r.tier || '—'}</span></Td>
              <Td>{r.status || '—'}</Td>
              <Td align="right">{r.modules    ?? '—'}</Td>
              <Td align="right">{r.subModules ?? '—'}</Td>
              <Td align="right">{r.screens    ?? '—'}</Td>
              <Td><code style={{ fontSize: 10, color: 'var(--text-muted)' }}>{r.moduleHash || '—'}</code></Td>
              <Td>
                {r.success
                  ? <span className="badge badge-green">✓ {t('erp.bulkResync.synced', 'Synced')}</span>
                  : r.skipped
                    ? <span className="badge badge-amber">⏭ {r.reason || t('erp.bulkResync.skipped', 'skipped')}</span>
                    : <span className="badge badge-red" title={r.error}>✗ {r.error || t('erp.bulkResync.failed', 'failed')}</span>}
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Main export ──────────────────────────────────────────────────────────────
export function BulkResyncResults({ result }) {
  const t   = useT();
  const tot = result?.totals      || {};
  const tier = result?.tierPushStep || {};

  const isError    = result.success === false;
  const hasWarning = !isError && tot.failed > 0;
  const bannerCls  = isError ? 'red' : (hasWarning ? 'amber' : 'green');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }} data-testid="bulk-resync-result">

      {/* Summary banner */}
      <div className={`alert-banner ${bannerCls}`}>
        {isError
          ? <AlertTriangle size={14} color="#dc2626" style={{ flexShrink: 0 }} />
          : <CheckCircle2  size={14} color={hasWarning ? '#d97706' : '#16a34a'} style={{ flexShrink: 0 }} />}
        <div style={{ fontSize: 12, color: isError ? '#991b1b' : (hasWarning ? '#92400e' : '#166534') }}>
          {isError
            ? <><strong>{t('erp.bulkResync.failedLabel', 'Bulk re-sync failed')}:</strong> {result.error || result.message}</>
            : <>
                <strong>{t('erp.bulkResync.completeLabel', 'Bulk re-sync complete')}</strong>
                {' · '}{tot.succeeded}/{tot.attempted} {t('erp.bulkResync.succeeded', 'succeeded')}
                {' · '}{tier.pushed || 0} {t('erp.bulkResync.tierRulesPushedTo', 'tier rules pushed to')} {tier.targetDB || 'RC26erp_dev'}
                {' · '}<Clock size={11} style={{ verticalAlign: 'middle' }} /> {result.elapsedMs}ms
              </>}
        </div>
      </div>

      {/* KPI grid */}
      {!isError && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 8 }}>
          <Kpi label={t('erp.bulkResync.kpi.tierPushed',      'Tier Rules Pushed')} value={tier.pushed      || 0} accent="#3b82f6" />
          <Kpi label={t('erp.bulkResync.kpi.attempted',       'Clients Attempted')} value={tot.attempted    || 0} accent="#8b5cf6" />
          <Kpi label={t('erp.bulkResync.kpi.succeeded',       'Succeeded')}         value={tot.succeeded    || 0} accent="#10b981" />
          <Kpi label={t('erp.bulkResync.kpi.failed',          'Failed')}            value={tot.failed       || 0} accent={tot.failed ? '#ef4444' : 'var(--text-muted)'} />
          <Kpi label={t('erp.bulkResync.kpi.skippedExpired',  'Skipped (expired)')} value={tot.skippedExpired || 0} accent="#f59e0b" />
        </div>
      )}

      <PerIndustryTable tier={tier} t={t} />
      <PerClientTable   clientResults={result.clientResults} t={t} />

      {/* Raw JSON */}
      <details>
        <summary style={{ cursor: 'pointer', fontSize: 11, color: '#3b82f6', fontWeight: 700 }}>
          {t('erp.bulkResync.viewRaw', 'View raw JSON')}
        </summary>
        <pre style={{
          background: '#0f172a', color: '#93c5fd', padding: 14, borderRadius: 6,
          fontFamily: 'monospace', fontSize: 10, marginTop: 8, maxHeight: 280, overflow: 'auto'
        }}>{JSON.stringify(result, null, 2)}</pre>
      </details>
    </div>
  );
}
